import { IEvent, IEventBlock } from "Bands/types";
import { copy } from "shared/assets";
import { Button, CalendarListTitle, FancyLink, Table } from "shared/components";
import { Message } from "shared/components/Message/Message";
import { Spacer } from "shared/layout";
import { IStatus, ITheme } from "shared/types";
import { renderCopy } from "../PushCopyModal";
import { API_ENDPOINTS } from "shared/constants";
import { StyledTitle } from "shared/components/Form/Fieldset/styles";

interface Props {
  showPushCopyModal: IEventBlock;
  setStep: (step: 1 | 2 | 3) => void;
  allEvents: IEvent[];
  selectedEvents: string[];
  onFinalSubmit: (data: {
    includeRsvpUsers: boolean;
    newEvents?: string[];
  }) => void;
  isLoading: boolean;
  completedRequests: string[];
  failedRequests: string[];
  setFailedRequests: (failedRequests: string[]) => void;
}

export const PushCopyStep3 = ({
  showPushCopyModal,
  setStep,
  allEvents,
  selectedEvents,
  onFinalSubmit,
  isLoading,
  completedRequests,
  failedRequests,
  setFailedRequests,
}: Props) => {
  return (
    <Spacer size="l">
      <StyledTitle>
        {renderCopy(
          copy.pushCopyBlock.step3.modalTitle,
          showPushCopyModal.type
        )}
      </StyledTitle>
      <Spacer size="l">
        <Table
          theme={ITheme.tertiary}
          columns={[
            {
              Header: "",
              accessor: "id",
              Cell: ({ value: id }) => {
                const event = allEvents.find((event) => event.id === id);

                if (event) {
                  const { title, type, date, band, bandName } = event;

                  return (
                    <CalendarListTitle
                      id={id}
                      title={title}
                      type={type}
                      date={date}
                      band={band}
                      bandName={bandName}
                      showFullDate
                      isLoading={
                        isLoading &&
                        !completedRequests?.includes(API_ENDPOINTS.EVENT(id))
                      }
                      isCompleted={completedRequests?.includes(
                        API_ENDPOINTS.EVENT(id)
                      )}
                      isFailed={failedRequests?.includes(
                        API_ENDPOINTS.EVENT(id)
                      )}
                      showAsSpan
                    />
                  );
                }

                return null;
              },
            },
          ]}
          options={{
            hideHeader: true,
          }}
          data={selectedEvents
            .map((eventId) => allEvents.find((e) => e.id === eventId))
            .sort(
              (a, b) =>
                new Date(a?.date!).getTime() - new Date(b?.date!).getTime()
            )}
        />
        <Spacer size="s" spaceDirection="horizontal">
          <Button
            type="button"
            size="l"
            theme={ITheme.primary}
            onClick={() => onFinalSubmit({ includeRsvpUsers: true })}
            disabled={isLoading || (failedRequests?.length ?? 0) > 0}
          >
            {copy.pushCopyBlock.step3.confirmAndCopy}
          </Button>
          <Button
            type="button"
            size="l"
            theme={ITheme.secondary}
            onClick={() => {
              setStep(2);
              setFailedRequests([]);
            }}
          >
            {copy.pushCopyBlock.step3.back}
          </Button>
        </Spacer>
        {failedRequests && failedRequests.length > 0 && (
          <Message status={IStatus.error}>
            {copy.pushCopyBlock.step3.failedToCopy}
            <FancyLink
              to={"#"}
              onClick={() => {
                onFinalSubmit({
                  includeRsvpUsers: true,
                  newEvents: failedRequests.map((e) => e.split("/").pop()!),
                });
              }}
            >
              {copy.pushCopyBlock.step3.clickHereToTryAgain}
            </FancyLink>
          </Message>
        )}
      </Spacer>
    </Spacer>
  );
};
